/*
 * File: Resources.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 30, 2025 at 2:20 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, forwardRef, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';
import { useNavigate } from 'react-router-dom';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';

// Components
import { Typography } from '../../Typography';
import { Button } from '../../Button';
import { LocaleLink } from '../../LocaleLink';
import { Resources as ResourcesComponent } from '../../Resources';
import { MomentaryAssessment } from '../../MomentaryAssessment';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../../constants/base.constants';

// Styles
import * as S from './Resources.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const Resources = forwardRef(({
	isVisible, className
}, ref) => {

	// Create state handlers
	const [isAssessmentVisible, setIsAssessmentVisible] = useState(true);
	const [showResources, setShowResources] = useState(isVisible);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.resources, [partner]);

	// Get actions from hooks
	const navigate = useNavigate();

	// Move to community
	const moveToCommunity = () => {

		// Navigate to community
		navigate('/');
	};

	// Perform actions on visibility change
	useEffect(() => {
		if (isVisible) {

			// Update show resources
			setShowResources(true);

		} else {

			// Update show resources
			setTimeout(() => {
				setShowResources(false);
			}, 300);

		}
	}, [isVisible]);

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					<Typography tag="h1" weight="black">
						{!partnerContent?.title ? (
							<Trans t={t} i18nKey="pageShareStory.resources.title">
								Thank you so much for sharing.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

					{/* Subtitle */}
					<Typography tag="p">
						{!partnerContent?.subtitle ? (
							<Trans t={t} i18nKey="pageShareStory.resources.subtitle">
								Every story makes a difference. Thank you for being a part of the
								{' '}
								{{ partnerName: partner.name }}
								{' '}
								community.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

				</S.ContentContainer>

				{/* Momentary Container */}
				{isAssessmentVisible && (
					<S.MomentaryContainer>

						{/* Momentary Assessment */}
						<MomentaryAssessment
							source={SOURCES.SHARE}
							onLoad={(isAvailable) => { setIsAssessmentVisible(isAvailable); }}
							onCompletion={() => { setIsAssessmentVisible(false); }}
						/>

					</S.MomentaryContainer>
				)}

				{/* Resources Container */}
				{showResources && (
					<S.ResourcesContainer>

						{/* Header */}
						<S.Header>

							{/* Title */}
							<Typography tag="h3" weight="black">
								<Trans t={t} i18nKey="pageShareStory.resources.findSupport">
									Find Support
								</Trans>
							</Typography>

							{/* See More */}
							<LocaleLink to="/resources">
								<Button variant="text">
									<Typography>
										<Trans t={t} i18nKey="actions.seeMore">
											See more
										</Trans>
									</Typography>
									<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
								</Button>
							</LocaleLink>
						</S.Header>

						{/* Resources */}
						<ResourcesComponent />

					</S.ResourcesContainer>
				)}

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" onClick={moveToCommunity}>
						<Typography>
							<Trans t={t} i18nKey="pageShareStory.resources.continueToCommunity">
								Continue to Community
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

			</S.InnerStep>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

Resources.displayName = 'Resources';
Resources.propTypes = {
	className: PropTypes.string,
	isVisible: PropTypes.bool
};
Resources.defaultProps = {
	className: null,
	isVisible: false
};
